import React, { useEffect, useState, useCallback } from 'react';
import { FiTrash2, FiX, FiChevronDown, FiChevronRight, FiStar, FiLoader } from 'react-icons/fi';
import { TbEraser } from 'react-icons/tb';
import { getApiBaseUrl, authenticatedFetch } from '../utils/api';
import {
  isAutoConsolidateEnabled,
  setAutoConsolidateEnabled,
  MEMORY_UPDATED_EVENT,
} from '../utils/memoryConsolidateSchedule';
import './MemoryPanel.css';

const SECTIONS = [
  { key: 'preferences', title: 'Preferences', empty: 'No preferences learned yet.', apiType: 'preference' },
  { key: 'rules',       title: 'Rules learned from corrections', empty: 'No rules yet. Rules are only saved when you explicitly correct KAI.', apiType: 'rule' },
  { key: 'facts',       title: 'Stable facts', empty: 'No stable facts learned yet.', apiType: 'fact' },
  { key: 'summaries',   title: 'Recent conversations', empty: 'No conversation summaries yet.', apiType: null },
];

const CONSOLIDATE_MODAL_TITLES = {
  preferences: 'Consolidate preferences',
  facts: 'Consolidate stable facts',
  rules: 'Consolidate rules',
};

const sortRows = (rows) =>
  [...rows].sort((a, b) => {
    const starDiff = (b.starred ? 1 : 0) - (a.starred ? 1 : 0);
    if (starDiff !== 0) return starDiff;
    return (b.updated_at || '').localeCompare(a.updated_at || '');
  });

const countNonStarred = (rows) => rows.filter((r) => !r.starred).length;

const updateRowInData = (prev, memoryKey, patch) => {
  const mapSection = (rows) =>
    sortRows(
      rows.map((r) => (r.memory_key === memoryKey ? { ...r, ...patch } : r))
    );
  return {
    preferences: mapSection(prev.preferences),
    facts: mapSection(prev.facts),
    rules: mapSection(prev.rules),
    summaries: prev.summaries,
  };
};

const MemoryPanel = ({ onClose, isOpen, onOpenConversation }) => {
  const [data, setData] = useState({ preferences: [], facts: [], rules: [], summaries: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({
    preferences: true,
    facts: false,
    rules: true,
    summaries: false,
  });
  const [busyKey, setBusyKey] = useState(null);
  const [forgetting, setForgetting] = useState(false);
  const [showForgetModal, setShowForgetModal] = useState(false);
  const [keepFavorites, setKeepFavorites] = useState(false);
  const [autoConsolidate, setAutoConsolidate] = useState(() => isAutoConsolidateEnabled());
  const [consolidatingSection, setConsolidatingSection] = useState(null);
  const [consolidatePreview, setConsolidatePreview] = useState(null);
  const [consolidateSection, setConsolidateSection] = useState(null);
  const [consolidateApplying, setConsolidateApplying] = useState(false);
  const [organizeMode, setOrganizeMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [batchDeleteKeys, setBatchDeleteKeys] = useState(null);
  const [batchDeleting, setBatchDeleting] = useState(false);

  const fetchMemory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch(`${getApiBaseUrl()}/memory`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Failed to load memory (${res.status}): ${txt}`);
      }
      const body = await res.json();
      setData({
        preferences: sortRows(body.preferences || []),
        facts: sortRows(body.facts || []),
        rules: sortRows(body.rules || []),
        summaries: body.summaries || [],
      });
    } catch (err) {
      console.error('Error loading memory:', err);
      setError(err.message || 'Failed to load memory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    fetchMemory();
  }, [isOpen, fetchMemory]);

  useEffect(() => {
    if (!isOpen) {
      setOrganizeMode(false);
      setSelectedKeys(new Set());
      setBatchDeleteKeys(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onUpdated = () => fetchMemory();
    window.addEventListener(MEMORY_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(MEMORY_UPDATED_EVENT, onUpdated);
  }, [isOpen, fetchMemory]);

  const handleDelete = async (memoryKey) => {
    if (!memoryKey) return;
    if (!window.confirm('Delete this memory? KAI will no longer remember it.')) return;

    setBusyKey(memoryKey);
    try {
      const res = await authenticatedFetch(
        `${getApiBaseUrl()}/memory/${encodeURIComponent(memoryKey)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Delete failed (${res.status}): ${txt}`);
      }
      setData((prev) => ({
        preferences: prev.preferences.filter((r) => r.memory_key !== memoryKey),
        facts:       prev.facts.filter((r) => r.memory_key !== memoryKey),
        rules:       prev.rules.filter((r) => r.memory_key !== memoryKey),
        summaries:   prev.summaries.filter((r) => r.memory_key !== memoryKey),
      }));
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to delete memory');
    } finally {
      setBusyKey(null);
    }
  };

  const enterOrganizeMode = () => {
    setOrganizeMode(true);
    setSelectedKeys(new Set());
    setExpanded((prev) => ({
      ...prev,
      preferences: true,
      facts: true,
      rules: true,
      // Summaries are not batch-deletable — leave their expand state alone.
    }));
  };

  const cancelOrganizeMode = () => {
    if (batchDeleting) return;
    setOrganizeMode(false);
    setSelectedKeys(new Set());
    setBatchDeleteKeys(null);
  };

  const toggleSelection = (memoryKey) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(memoryKey)) next.delete(memoryKey);
      else next.add(memoryKey);
      return next;
    });
  };

  const openBatchDeleteModal = () => {
    if (selectedKeys.size === 0) return;
    setBatchDeleteKeys([...selectedKeys]);
  };

  const closeBatchDeleteModal = () => {
    if (batchDeleting) return;
    setBatchDeleteKeys(null);
  };

  const confirmBatchDelete = async () => {
    if (!batchDeleteKeys || batchDeleteKeys.length === 0) return;
    setBatchDeleting(true);
    try {
      const res = await authenticatedFetch(`${getApiBaseUrl()}/memory/delete-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memory_keys: batchDeleteKeys }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Batch delete failed (${res.status}): ${txt}`);
      }
      const removed = new Set(batchDeleteKeys);
      setData((prev) => ({
        preferences: prev.preferences.filter((r) => !removed.has(r.memory_key)),
        facts: prev.facts.filter((r) => !removed.has(r.memory_key)),
        rules: prev.rules.filter((r) => !removed.has(r.memory_key)),
        summaries: prev.summaries.filter((r) => !removed.has(r.memory_key)),
      }));
      setSelectedKeys(new Set());
      setBatchDeleteKeys(null);
      setOrganizeMode(false);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to delete memories');
      fetchMemory();
    } finally {
      setBatchDeleting(false);
    }
  };

  const handleStar = async (memoryKey, starred) => {
    if (!memoryKey) return;

    const previous = data;
    setBusyKey(memoryKey);
    setData((prev) => updateRowInData(prev, memoryKey, { starred }));

    try {
      const res = await authenticatedFetch(
        `${getApiBaseUrl()}/memory/${encodeURIComponent(memoryKey)}/star`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ starred }),
        }
      );
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Update failed (${res.status}): ${txt}`);
      }
      const body = await res.json();
      if (body.row) {
        setData((prev) => updateRowInData(prev, memoryKey, body.row));
      }
    } catch (err) {
      console.error(err);
      setData(previous);
      alert(err.message || 'Failed to update favorite');
    } finally {
      setBusyKey(null);
    }
  };

  const closeConsolidateModal = () => {
    if (consolidateApplying) return;
    setConsolidatePreview(null);
    setConsolidateSection(null);
  };

  const handleConsolidatePreview = async (sectionKey, apiType) => {
    if (!apiType || consolidatingSection) return;
    setConsolidatingSection(sectionKey);
    setConsolidatePreview(null);
    setConsolidateSection(sectionKey);
    try {
      const res = await authenticatedFetch(`${getApiBaseUrl()}/memory/consolidate/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: apiType }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Consolidate preview failed (${res.status}): ${txt}`);
      }
      const body = await res.json();
      setConsolidatePreview(body);
    } catch (err) {
      console.error(err);
      setConsolidateSection(null);
      alert(err.message || 'Failed to analyze duplicates');
    } finally {
      setConsolidatingSection(null);
    }
  };

  const handleConsolidateApply = async () => {
    if (!consolidatePreview?.proposal_id) return;
    setConsolidateApplying(true);
    try {
      const res = await authenticatedFetch(`${getApiBaseUrl()}/memory/consolidate/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          proposal_id: consolidatePreview.proposal_id,
          type: consolidatePreview.type,
          groups: consolidatePreview.groups || [],
          orphan_deletions: consolidatePreview.orphan_deletions || [],
        }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Apply failed (${res.status}): ${txt}`);
      }
      closeConsolidateModal();
      await fetchMemory();
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to apply consolidation');
    } finally {
      setConsolidateApplying(false);
    }
  };

  const openForgetModal = () => {
    setKeepFavorites(false);
    setShowForgetModal(true);
  };

  const closeForgetModal = () => {
    if (forgetting) return;
    setShowForgetModal(false);
    setKeepFavorites(false);
  };

  const handleForgetAll = async () => {
    setForgetting(true);
    try {
      const res = await authenticatedFetch(`${getApiBaseUrl()}/memory/forget-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keep_favorites: keepFavorites }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Forget-all failed (${res.status}): ${txt}`);
      }
      if (keepFavorites) {
        await fetchMemory();
      } else {
        setData({ preferences: [], facts: [], rules: [], summaries: [] });
      }
      setShowForgetModal(false);
      setKeepFavorites(false);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to forget memory');
    } finally {
      setForgetting(false);
    }
  };

  const handleAutoConsolidateToggle = (e) => {
    const enabled = e.target.checked;
    setAutoConsolidate(enabled);
    setAutoConsolidateEnabled(enabled);
  };

  const toggleSection = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const totalRows =
    data.preferences.length + data.facts.length + data.rules.length + data.summaries.length;
  const organizableCount =
    data.preferences.length + data.facts.length + data.rules.length;

  const consolidateStats = consolidatePreview?.stats;
  const consolidateGroups = consolidatePreview?.groups || [];
  const hasConsolidateWork = consolidateGroups.length > 0;

  return (
    <div className="memory-panel">
      <div className="memory-header">
        <div className="memory-header-content">
          <h3>What KAI remembers about you</h3>
        </div>
        <button
          className="close-sidebar-btn"
          onClick={onClose}
          title="Close memory panel"
          aria-label="Close memory panel"
        >
          <FiX size={16} />
        </button>
      </div>

      <div className="memory-body">
        {loading && <div className="memory-loading">Loading memory…</div>}

        {error && (
          <div className="memory-error">
            <p>{error}</p>
            <button onClick={fetchMemory} className="memory-retry-btn">Retry</button>
          </div>
        )}

        {!loading && !error && totalRows === 0 && (
          <div className="memory-empty-state">
            <p>
              KAI hasn&apos;t learned anything about you yet. As you chat,
              preferences, facts and summaries will appear here. Star items
              as favorites so KAI prioritizes them, or delete any memory at
              any time.
            </p>
          </div>
        )}

        {!loading && !error && totalRows > 0 && SECTIONS.map(({ key, title, empty, apiType }) => {
          const rows = data[key] || [];
          const isSectionOpen = expanded[key];
          const nonStarred = countNonStarred(rows);
          const showBroom = apiType && nonStarred >= 2;
          const broomBusy = consolidatingSection === key;
          return (
            <section className="memory-section" key={key}>
              <div className="memory-section-header-row">
                <button
                  className="memory-section-header"
                  onClick={() => toggleSection(key)}
                  aria-expanded={isSectionOpen}
                >
                  {isSectionOpen ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                  <span className="memory-section-title">{title}</span>
                </button>
                <span className="memory-section-header-actions">
                  {showBroom && (
                    <button
                      type="button"
                      className={`memory-section-consolidate-btn${broomBusy ? ' loading' : ''}`}
                      onClick={() => handleConsolidatePreview(key, apiType)}
                      disabled={
                        !!consolidatingSection
                        || !!busyKey
                        || forgetting
                        || consolidateApplying
                        || organizeMode
                      }
                      title={`Consolidate duplicate ${apiType === 'fact' ? 'facts' : `${apiType}s`}`}
                      aria-label={`Consolidate duplicate ${apiType === 'fact' ? 'facts' : `${apiType}s`}`}
                    >
                      {broomBusy ? <FiLoader size={14} className="memory-spin" /> : <TbEraser size={14} />}
                    </button>
                  )}
                  <span className="memory-section-count">{rows.length}</span>
                </span>
              </div>
              {isSectionOpen && (
                <div className="memory-section-body">
                  {rows.length === 0 && (
                    <p className="memory-section-empty">{empty}</p>
                  )}
                  {rows.map((row) => (
                    <MemoryRow
                      key={row.memory_key}
                      row={row}
                      onDelete={handleDelete}
                      onStar={handleStar}
                      busy={busyKey === row.memory_key}
                      onOpenConversation={onOpenConversation}
                      organizeMode={organizeMode && key !== 'summaries'}
                      selected={selectedKeys.has(row.memory_key)}
                      onToggleSelect={() => toggleSelection(row.memory_key)}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      <div className="memory-footer">
        {organizeMode ? (
          <div className="memory-footer-organize">
            <button
              type="button"
              className="memory-delete-selected-btn"
              onClick={openBatchDeleteModal}
              disabled={selectedKeys.size === 0 || batchDeleting}
            >
              Delete selected ({selectedKeys.size})
            </button>
            <button
              type="button"
              className="memory-cancel-organize-btn"
              onClick={cancelOrganizeMode}
              disabled={batchDeleting}
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <label className="memory-auto-consolidate-checkbox">
              <input
                type="checkbox"
                checked={autoConsolidate}
                onChange={handleAutoConsolidateToggle}
              />
              <span>Automatically clean duplicate memories once a week</span>
            </label>
            {organizableCount > 0 && (
              <button
                type="button"
                className="memory-organize-btn"
                onClick={enterOrganizeMode}
                disabled={forgetting || !!consolidatingSection || consolidateApplying}
              >
                Organize memories
              </button>
            )}
            <button
              type="button"
              className="memory-forget-btn"
              onClick={openForgetModal}
              disabled={forgetting || totalRows === 0}
              title="Delete every memory KAI has about you"
            >
              Forget everything
            </button>
          </>
        )}
      </div>

      {consolidateSection && (consolidatePreview || consolidatingSection === consolidateSection) && (
        <div className="memory-modal-overlay" onClick={closeConsolidateModal} role="presentation">
          <div
            className="memory-modal memory-modal-wide"
            role="dialog"
            aria-labelledby="memory-consolidate-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="memory-consolidate-title">
              {CONSOLIDATE_MODAL_TITLES[consolidateSection] || 'Consolidate memories'}
            </h4>
            {consolidatingSection === consolidateSection && !consolidatePreview && (
              <div className="memory-consolidate-loading" role="status" aria-live="polite">
                <FiLoader size={28} className="memory-spin" aria-hidden="true" />
                <p>Analyzing duplicates…</p>
                <p className="memory-consolidate-loading-hint">This can take a minute for large lists.</p>
              </div>
            )}
            {consolidatePreview && (
              <>
                {hasConsolidateWork ? (
                  <p>
                    {consolidateStats?.candidates ?? 0} records → {consolidateStats?.will_merge_into ?? 0}{' '}
                    consolidated ({consolidateStats?.will_delete ?? 0} removed)
                  </p>
                ) : (
                  <p>No duplicate records found in this category.</p>
                )}
                <p className="memory-consolidate-note">Favorites are never changed.</p>
                {hasConsolidateWork && (
                  <ul className="memory-consolidate-groups">
                    {consolidateGroups.map((group, idx) => (
                      <li key={idx} className="memory-consolidate-group">
                        <div className="memory-consolidate-merged">{group.merged_content}</div>
                        <div className="memory-consolidate-replaces">
                          Replaces {group.source_keys.length} record{group.source_keys.length === 1 ? '' : 's'}
                          {group.reason ? ` — ${group.reason}` : ''}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="memory-modal-actions">
                  <button
                    type="button"
                    className="memory-modal-cancel"
                    onClick={closeConsolidateModal}
                    disabled={consolidateApplying}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="memory-modal-apply"
                    onClick={handleConsolidateApply}
                    disabled={consolidateApplying || !hasConsolidateWork}
                  >
                    {consolidateApplying ? 'Applying…' : 'Apply consolidation'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {batchDeleteKeys && (
        <div className="memory-modal-overlay" onClick={closeBatchDeleteModal} role="presentation">
          <div
            className="memory-modal"
            role="dialog"
            aria-labelledby="memory-batch-delete-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="memory-batch-delete-title">
              {batchDeleteKeys.length === 1
                ? 'Delete this memory?'
                : `Delete ${batchDeleteKeys.length} memories?`}
            </h4>
            <p>
              {batchDeleteKeys.length === 1
                ? 'KAI will no longer remember this. This cannot be undone.'
                : 'KAI will no longer remember these items. This cannot be undone.'}
            </p>
            <div className="memory-modal-actions">
              <button
                type="button"
                className="memory-modal-cancel"
                onClick={closeBatchDeleteModal}
                disabled={batchDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="memory-modal-confirm"
                onClick={confirmBatchDelete}
                disabled={batchDeleting}
              >
                {batchDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showForgetModal && (
        <div className="memory-modal-overlay" onClick={closeForgetModal} role="presentation">
          <div
            className="memory-modal"
            role="dialog"
            aria-labelledby="memory-forget-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="memory-forget-title">Forget everything?</h4>
            <p>
              This permanently deletes preferences, facts, rules, and conversation
              summaries KAI has saved across past conversations. Your chat
              conversations themselves are not deleted.
            </p>
            <label className="memory-forget-checkbox">
              <input
                type="checkbox"
                checked={keepFavorites}
                onChange={(e) => setKeepFavorites(e.target.checked)}
                disabled={forgetting}
              />
              <span>Keep my favorite memories</span>
            </label>
            <div className="memory-modal-actions">
              <button
                type="button"
                className="memory-modal-cancel"
                onClick={closeForgetModal}
                disabled={forgetting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="memory-modal-confirm"
                onClick={handleForgetAll}
                disabled={forgetting}
              >
                {forgetting ? 'Forgetting…' : 'Forget everything'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MemoryRow = ({
  row,
  onDelete,
  onStar,
  busy,
  onOpenConversation,
  organizeMode = false,
  selected = false,
  onToggleSelect,
}) => {
  const lastSeen = (row.updated_at || row.last_accessed_at || '').slice(0, 10);
  const confidencePct = row.confidence ? Math.round(row.confidence * 100) : null;
  const isSummary = row.type === 'summary';
  const canStar = !isSummary;
  const canOpen = isSummary && !!row.source_conversation_id && typeof onOpenConversation === 'function';

  const handleOpen = () => {
    if (!canOpen || organizeMode) return;
    onOpenConversation(row.source_conversation_id);
  };

  const handleRowClick = () => {
    if (organizeMode && onToggleSelect) onToggleSelect();
  };

  return (
    <div
      className={`memory-row${row.starred ? ' favorite' : ''}${organizeMode ? ' selectable' : ''}${selected ? ' selected' : ''}`}
      onClick={organizeMode ? handleRowClick : undefined}
    >
      {organizeMode && (
        <input
          type="checkbox"
          className="memory-select-checkbox"
          checked={selected}
          onChange={handleRowClick}
          onClick={(e) => e.stopPropagation()}
          aria-label="Select memory"
        />
      )}
      <div className="memory-row-main">
        {canOpen && !organizeMode ? (
          <button
            type="button"
            className="memory-row-content memory-row-content-link"
            onClick={handleOpen}
            title="Open this conversation"
          >
            {row.content}
          </button>
        ) : (
          <div className="memory-row-content">
            {row.content}
          </div>
        )}
        <div className="memory-row-meta">
          {row.starred && (
            <span className="memory-favorite-badge">Favorite</span>
          )}
          {confidencePct !== null && !isSummary && (
            <span
              className={`memory-pill ${
                confidencePct >= 80 ? 'high' : confidencePct >= 60 ? 'med' : 'low'
              }`}
              title={`Confidence: ${confidencePct}%`}
            >
              {confidencePct}%
            </span>
          )}
          {lastSeen && <span className="memory-date">last seen {lastSeen}</span>}
          {isSummary && row.topics && row.topics.length > 0 && (
            <span className="memory-topics">
              {row.topics.slice(0, 3).join(' · ')}
            </span>
          )}
        </div>
        {isSummary && row.reason && (
          <div className="memory-row-reason" title="Why KAI kept this summary">
            {row.reason}
          </div>
        )}
      </div>
      {!organizeMode && (
        <div className="memory-row-actions">
          {canStar && (
            <button
              type="button"
              className={`memory-star-btn${row.starred ? ' starred' : ''}`}
              onClick={() => onStar(row.memory_key, !row.starred)}
              disabled={busy}
              title={row.starred ? 'Remove from favorites' : 'Mark as favorite'}
              aria-label={row.starred ? 'Remove from favorites' : 'Mark as favorite'}
              aria-pressed={!!row.starred}
            >
              <FiStar size={14} />
            </button>
          )}
          <button
            type="button"
            className="memory-delete-btn"
            onClick={() => onDelete(row.memory_key)}
            disabled={busy}
            title="Delete this memory"
            aria-label="Delete memory"
          >
            <FiTrash2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default MemoryPanel;
